import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Info, MapPin, ShieldCheck, Utensils, FileText, Activity, ArrowRight, Home, Plane, Wallet, Sun, Users } from 'lucide-react';
import { destinations } from '../data/destinations';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
export function Destinations() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-426">
      <Helmet data-id="element-427">
        <title data-id="element-428">Medical Tourism Destinations | Affordable Medical Tourism</title>
        <meta name="description" content="Explore top global destinations for medical tourism. Learn about the specialized healthcare infrastructure, clinical expertise, and illustrative savings in countries like Mexico, Thailand, and Turkey." data-id="element-429" />
        <link rel="canonical" href="https://affordablemedicaltourism.com/destinations" data-id="element-430" />
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" crossOrigin="" data-id="element-431" />
      </Helmet>

      <AppHeader data-id="element-432" />

      <main className="flex-1 py-20 px-6 md:px-12 max-w-7xl mx-auto w-full" data-id="element-433">
        <div className="text-center mb-20" data-id="element-434">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-435">
            Global Medical Destinations
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-436">
            We partner with elite healthcare institutions in the world's most
            established medical travel hubs. Each destination offers a unique
            combination of clinical specialization, advanced infrastructure, and
            significant cost efficiencies.
          </p>
        </div>

        <div className="space-y-32" data-id="element-437">
          {destinations.map((dest, index) => <div key={dest.slug} className="flex flex-col gap-12" data-id="element-438">
              <div className={`flex flex-col lg:flex-row gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`} data-id="element-439">
                <div className="w-full lg:w-1/2" data-id="element-440">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] cursor-pointer group" onClick={() => navigate(`/destinations/${dest.slug}`)} role="button" tabIndex={0} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate(`/destinations/${dest.slug}`);
                }
              }} aria-label={`Learn more about medical tourism in ${dest.country}`} data-id="element-441">
                    <img src={dest.heroImage} alt={`Healthcare infrastructure in ${dest.country}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-442" />
                    <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-slate-900/20 transition-colors duration-300" data-id="element-443" />
                  </div>
                </div>

                <div className="w-full lg:w-1/2 space-y-6" data-id="element-444">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 cursor-pointer hover:text-blue-700 transition-colors" onClick={() => navigate(`/destinations/${dest.slug}`)} data-id="element-445">
                    {dest.country}
                  </h2>

                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-446">
                    {dest.knownFor.map(specialty => <Badge key={specialty} variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-50 text-sm" data-id="element-447">
                        {specialty}
                      </Badge>)}
                  </div>

                  <div className="prose prose-slate prose-lg text-slate-600" data-id="element-448">
                    {dest.content.split('\n\n').map((paragraph, idx) => <p key={idx} data-id="element-449">{paragraph.trim()}</p>)}
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mt-8" data-id="element-450">
                    <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-451">
                      <div data-id="element-452">
                        <div className="text-sm text-slate-500 mb-1 flex items-center gap-1" data-id="element-453">
                          <Info className="w-4 h-4" data-id="element-454" /> Illustrative Savings vs
                          US
                        </div>
                        <div className="text-2xl font-bold text-green-700" data-id="element-455">
                          {dest.illustrativeSavingsRange}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 w-full sm:w-auto" data-id="element-456">
                        <button onClick={() => navigate(`/destinations/${dest.slug}`)} className="text-blue-700 hover:text-blue-800 font-medium flex items-center gap-2 transition-colors" aria-label={`Learn more about ${dest.country}`} data-id="element-457">
                          Learn more <ArrowRight className="w-4 h-4" data-id="element-458" />
                        </button>
                        <Button onClick={() => navigate('/contact')} data-id="element-459">
                          Inquire about {dest.country}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Data Section */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-460">
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-461">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200" data-id="element-462">
                    <div className="flex items-center gap-2 mb-4 text-blue-700" data-id="element-463">
                      <Activity className="w-5 h-5" data-id="element-464" />
                      <h3 className="font-semibold text-slate-900" data-id="element-465">
                        Popular Procedures
                      </h3>
                    </div>
                    <ul className="list-disc pl-5 space-y-1 text-slate-600 text-sm" data-id="element-466">
                      {dest.popularProcedures.map(proc => <li key={proc} data-id="element-467">{proc}</li>)}
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200" data-id="element-468">
                    <div className="flex items-center gap-2 mb-4 text-blue-700" data-id="element-469">
                      <ShieldCheck className="w-5 h-5" data-id="element-470" />
                      <h3 className="font-semibold text-slate-900" data-id="element-471">
                        Accreditations & Stats
                      </h3>
                    </div>
                    <ul className="list-disc pl-5 space-y-1 text-slate-600 text-sm mb-4" data-id="element-472">
                      {dest.accreditations.map(acc => <li key={acc} data-id="element-473">{acc}</li>)}
                    </ul>
                    <p className="text-sm text-slate-600 border-t border-slate-100 pt-3" data-id="element-474">
                      <strong data-id="element-475">Stats:</strong> {dest.hospitalStats}
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200" data-id="element-476">
                    <div className="flex items-center gap-2 mb-4 text-blue-700" data-id="element-477">
                      <FileText className="w-5 h-5" data-id="element-478" />
                      <h3 className="font-semibold text-slate-900" data-id="element-479">
                        Visa Requirements
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600" data-id="element-480">
                      {dest.visaRequirements}
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200" data-id="element-481">
                    <div className="flex items-center gap-2 mb-4 text-blue-700" data-id="element-482">
                      <Utensils className="w-5 h-5" data-id="element-483" />
                      <h3 className="font-semibold text-slate-900" data-id="element-484">
                        Food Options
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600" data-id="element-485">{dest.foodOptions}</p>
                  </div>
                </div>

                <div className="bg-white p-2 rounded-2xl shadow-sm border border-slate-200 h-[350px] lg:h-auto relative z-0" data-id="element-486">
                  <MapContainer center={dest.coordinates} zoom={5} scrollWheelZoom={false} style={{
                height: '100%',
                width: '100%',
                borderRadius: '0.75rem'
              }} data-id="element-487">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-488" />
                    <Marker position={dest.coordinates} data-id="element-489">
                      <Popup data-id="element-490">Medical hub in {dest.country}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>

              {/* Settlement Cities Section */}
              {dest.settlementCities && dest.settlementCities.length > 0 && <div className="bg-blue-900 rounded-3xl p-8 md:p-12" data-id="element-491">
                  <div className="flex items-center gap-3 mb-2 text-blue-200" data-id="element-492">
                    <Home className="w-5 h-5" data-id="element-493" />
                    <span className="text-sm font-semibold uppercase tracking-wide" data-id="element-494">
                      Settle Where You Heal
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-495">
                    Global Medical Tourism Cities in {dest.country}
                  </h3>
                  <p className="text-blue-100 max-w-3xl mb-8 leading-relaxed" data-id="element-496">
                    For long recovery periods, ongoing treatment, or retirement,
                    these cities pair immediate access to accredited hospitals
                    with low living costs, established expat communities, and
                    long-stay visa pathways. No wait lists, assisted-living
                    support, and a recovery-friendly lifestyle.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-497">
                    {dest.settlementCities.map(city => <div key={city.name} className="bg-white rounded-2xl p-6 shadow-lg" data-id="element-498">
                        <div className="flex items-center gap-2 mb-3" data-id="element-499">
                          <MapPin className="w-5 h-5 text-blue-700" data-id="element-500" />
                          <h4 className="text-lg font-bold text-slate-900" data-id="element-501">
                            {city.name}
                          </h4>
                        </div>
                        <p className="text-sm text-slate-600 mb-5 leading-relaxed" data-id="element-502">
                          {city.whySettle}
                        </p>
                        <dl className="space-y-3 mb-5" data-id="element-503">
                          <div className="flex items-start gap-2" data-id="element-504">
                            <Wallet className="w-4 h-4 text-green-700 mt-0.5 shrink-0" data-id="element-505" />
                            <div data-id="element-506">
                              <dt className="text-xs font-semibold text-slate-500" data-id="element-507">
                                Cost of Living vs US
                              </dt>
                              <dd className="text-sm text-slate-800" data-id="element-508">
                                {city.costOfLivingVsUS}
                              </dd>
                            </div>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-509">
                            <Sun className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" data-id="element-510" />
                            <div data-id="element-511">
                              <dt className="text-xs font-semibold text-slate-500" data-id="element-512">
                                Climate
                              </dt>
                              <dd className="text-sm text-slate-800" data-id="element-513">
                                {city.climate}
                              </dd>
                            </div>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-514">
                            <Users className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" data-id="element-515" />
                            <div data-id="element-516">
                              <dt className="text-xs font-semibold text-slate-500" data-id="element-517">
                                Expat Community
                              </dt>
                              <dd className="text-sm text-slate-800" data-id="element-518">
                                {city.expatCommunity}
                              </dd>
                            </div>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-519">
                            <ShieldCheck className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" data-id="element-520" />
                            <div data-id="element-521">
                              <dt className="text-xs font-semibold text-slate-500" data-id="element-522">
                                Hospital Proximity
                              </dt>
                              <dd className="text-sm text-slate-800" data-id="element-523">
                                {city.hospitalProximity}
                              </dd>
                            </div>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-524">
                            <Home className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" data-id="element-525" />
                            <div data-id="element-526">
                              <dt className="text-xs font-semibold text-slate-500" data-id="element-527">
                                Assisted Living
                              </dt>
                              <dd className="text-sm text-slate-800" data-id="element-528">
                                {city.assistedLivingAvailability}
                              </dd>
                            </div>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-529">
                            <FileText className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" data-id="element-530" />
                            <div data-id="element-531">
                              <dt className="text-xs font-semibold text-slate-500" data-id="element-532">
                                Long-Stay Visa Pathway
                              </dt>
                              <dd className="text-sm text-slate-800" data-id="element-533">
                                {city.longStayVisaPathway}
                              </dd>
                            </div>
                          </div>
                        </dl>
                        <div className="bg-green-50 rounded-lg px-4 py-3 mb-5" data-id="element-534">
                          <div className="text-xs font-semibold text-green-700 mb-1" data-id="element-535">
                            Typical Monthly Budget
                          </div>
                          <div className="text-base font-bold text-green-800" data-id="element-536">
                            {city.typicalMonthlyBudget}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-5" data-id="element-537">
                          {city.bestSuitedFor.map(profile => <Badge key={profile} variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-xs" data-id="element-538">
                              {profile}
                            </Badge>)}
                        </div>
                        <Button className="w-full" onClick={() => navigate('/contact')} data-id="element-539">
                          Plan your stay in {city.name}
                        </Button>
                      </div>)}
                  </div>
                </div>}
            </div>)}
        </div>

        {/* End-of-page conversion band */}
        <section className="mt-32 bg-blue-700 rounded-3xl px-8 py-16 md:px-16 text-center" data-id="element-540">
          <Plane className="w-10 h-10 text-blue-200 mx-auto mb-6" data-id="element-541" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-542">
            Skip the wait list. Start your care plan today.
          </h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-543">
            Tell us your procedure and preferred destination. A care specialist
            will build a personalized, no-obligation plan with illustrative
            savings, accredited facilities, and travel logistics. There is no
            cost to ask.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-544">
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50" onClick={() => navigate('/contact')} data-id="element-545">
              Get your free quote
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-blue-800 hover:text-white hover:border-white" onClick={() => navigate('/how-it-works')} data-id="element-546">
              See how it works
            </Button>
          </div>
          <p className="text-blue-200 text-sm mt-6" data-id="element-547">
            All inquiries are handled through our secure contact form. A
            specialist will follow up with your tailored options.
          </p>
        </section>
      </main>

      <AppFooter data-id="element-548" />
    </div>;
}